import { useState, useCallback, useRef } from 'react'
import { Header } from './components/Header'
import { Footer } from './components/Footer'
import { ContentSection } from './components/ContentSection'
import { useI18n } from './i18n/index.tsx'
import {
  Copy, Check, RefreshCw, Plus, Trash2, Download, Palette
} from 'lucide-react'

interface ColorStop {
  id: string
  color: string
  position: number
}

type GradientType = 'linear' | 'radial' | 'conic'

const PRESETS: { name: string; type: GradientType; angle: number; stops: { color: string; position: number }[] }[] = [
  { name: 'Sunset', type: 'linear', angle: 135, stops: [{ color: '#FF6B6B', position: 0 }, { color: '#FFE66D', position: 50 }, { color: '#4ECDC4', position: 100 }] },
  { name: 'Ocean', type: 'linear', angle: 180, stops: [{ color: '#0066FF', position: 0 }, { color: '#00CCFF', position: 100 }] },
  { name: 'Purple Haze', type: 'linear', angle: 45, stops: [{ color: '#667eea', position: 0 }, { color: '#764ba2', position: 100 }] },
  { name: 'Fire', type: 'linear', angle: 90, stops: [{ color: '#f12711', position: 0 }, { color: '#f5af19', position: 100 }] },
  { name: 'Mint', type: 'linear', angle: 120, stops: [{ color: '#11998e', position: 0 }, { color: '#38ef7d', position: 100 }] },
  { name: 'Night Sky', type: 'radial', angle: 0, stops: [{ color: '#0f2027', position: 0 }, { color: '#203a43', position: 50 }, { color: '#2c5364', position: 100 }] },
  { name: 'Rainbow', type: 'conic', angle: 0, stops: [{ color: '#ff0000', position: 0 }, { color: '#ff7f00', position: 17 }, { color: '#ffff00', position: 33 }, { color: '#00ff00', position: 50 }, { color: '#0000ff', position: 67 }, { color: '#4b0082', position: 83 }, { color: '#ff0000', position: 100 }] },
  { name: 'Cotton Candy', type: 'linear', angle: 160, stops: [{ color: '#ff9a9e', position: 0 }, { color: '#fecfef', position: 50 }, { color: '#fecfef', position: 100 }] },
]

function generateGradientCSS(type: GradientType, angle: number, stops: ColorStop[]): string {
  const sorted = [...stops].sort((a, b) => a.position - b.position)
  const stopsStr = sorted.map(s => `${s.color} ${s.position}%`).join(', ')

  switch (type) {
    case 'linear':
      return `linear-gradient(${angle}deg, ${stopsStr})`
    case 'radial':
      return `radial-gradient(circle, ${stopsStr})`
    case 'conic':
      return `conic-gradient(from ${angle}deg, ${stopsStr})`
  }
}

function randomColor(): string {
  const letters = '0123456789ABCDEF'
  let color = '#'
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)]
  }
  return color
}

function generateRandomStops(count: number = 2): ColorStop[] {
  const stops: ColorStop[] = []
  for (let i = 0; i < count; i++) {
    stops.push({
      id: Math.random().toString(36).substring(2, 15),
      color: randomColor(),
      position: Math.round((i / (count - 1)) * 100),
    })
  }
  return stops
}

function App() {
  const { t } = useI18n()
  const [type, setType] = useState<GradientType>('linear')
  const [angle, setAngle] = useState(135)
  const [stops, setStops] = useState<ColorStop[]>([
    { id: '1', color: '#FF6B6B', position: 0 },
    { id: '2', color: '#4ECDC4', position: 100 },
  ])
  const [copied, setCopied] = useState(false)
  const [activeStopId, setActiveStopId] = useState<string | null>(null)
  const gradientRef = useRef<HTMLDivElement>(null)

  const cssCode = generateGradientCSS(type, angle, stops)
  const cssDeclaration = `background: ${cssCode};`

  const addStop = useCallback(() => {
    const sorted = [...stops].sort((a, b) => a.position - b.position)
    let newPos = 50
    if (sorted.length > 1) {
      // Find largest gap
      let maxGap = 0
      let gapPos = 50
      for (let i = 0; i < sorted.length - 1; i++) {
        const gap = sorted[i + 1].position - sorted[i].position
        if (gap > maxGap) {
          maxGap = gap
          gapPos = sorted[i].position + gap / 2
        }
      }
      newPos = Math.round(gapPos)
    }
    const newStop: ColorStop = {
      id: Math.random().toString(36).substring(2, 15),
      color: randomColor(),
      position: newPos,
    }
    setStops(prev => [...prev, newStop])
    setActiveStopId(newStop.id)
  }, [stops])

  const removeStop = useCallback((id: string) => {
    if (stops.length <= 2) return
    setStops(prev => prev.filter(s => s.id !== id))
    if (activeStopId === id) setActiveStopId(null)
  }, [stops.length, activeStopId])

  const updateStop = useCallback((id: string, updates: Partial<ColorStop>) => {
    setStops(prev => prev.map(s => s.id === id ? { ...s, ...updates } : s))
  }, [])

  const loadPreset = useCallback((preset: typeof PRESETS[0]) => {
    setType(preset.type)
    setAngle(preset.angle)
    setStops(preset.stops.map((s, i) => ({ ...s, id: Math.random().toString(36).substring(2, 15) + i })))
    setActiveStopId(null)
  }, [])

  const randomize = useCallback(() => {
    const types: GradientType[] = ['linear', 'radial', 'conic']
    setType(types[Math.floor(Math.random() * types.length)])
    setAngle(Math.floor(Math.random() * 360))
    setStops(generateRandomStops(Math.floor(Math.random() * 3) + 2))
    setActiveStopId(null)
  }, [])

  const copyCSS = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(cssDeclaration)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {}
  }, [cssDeclaration])

  const downloadPNG = useCallback(() => {
    if (!gradientRef.current) return
    const canvas = document.createElement('canvas')
    canvas.width = 1920
    canvas.height = 1080
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height)
    const sorted = [...stops].sort((a, b) => a.position - b.position)
    sorted.forEach(s => gradient.addColorStop(s.position / 100, s.color))
    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    canvas.toBlob(blob => {
      if (!blob) return
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `gradient-${Date.now()}.png`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    })
  }, [stops])

  return (
    <div className="min-h-screen bg-background flex flex-col transition-colors duration-200">
      <Header />

      {/* Hero */}
      <div className="text-center py-6 px-4">
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
          {t('gradient.title') || 'Gradient Generator'}
        </h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          {t('gradient.subtitle') || 'Create beautiful CSS gradients. Linear, radial, conic. '}
          <span className="text-primary font-medium">{t('gradient.privacy') || '100% client-side'}</span>.
        </p>
      </div>

      <main className="flex-1 w-full max-w-6xl mx-auto px-4 pb-8 space-y-4">
        {/* Gradient Preview */}
        <div
          ref={gradientRef}
          className="rounded-xl shadow-sm border border-border overflow-hidden h-64 md:h-80 relative"
          style={{ background: cssCode }}
        >
          <div className="absolute bottom-4 right-4 flex gap-2">
            <button
              onClick={copyCSS}
              className="px-3 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg text-sm font-medium hover:bg-white/30 transition-colors"
            >
              {copied ? <Check className="w-4 h-4 inline mr-1" /> : <Copy className="w-4 h-4 inline mr-1" />}
              {copied ? (t('actions.copied') || 'Copied!') : 'CSS'}
            </button>
            <button
              onClick={downloadPNG}
              className="px-3 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg text-sm font-medium hover:bg-white/30 transition-colors"
            >
              <Download className="w-4 h-4 inline mr-1" />
              PNG
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-surface rounded-xl shadow-sm border border-border p-4 md:p-6 space-y-4">
          {/* Type */}
          <div>
            <label className="text-sm font-medium text-foreground block mb-2">{t('gradient.type') || 'Type'}</label>
            <div className="flex flex-wrap gap-2">
              {(['linear', 'radial', 'conic'] as GradientType[]).map(gt => (
                <button
                  key={gt}
                  onClick={() => setType(gt)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    type === gt ? 'bg-brand-primary text-white' : 'bg-muted text-foreground hover:bg-muted/80'
                  }`}
                >
                  {t(`gradient.${gt}`) || gt}
                </button>
              ))}
            </div>
          </div>

          {/* Angle (for linear/conic) */}
          {type !== 'radial' && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-foreground">{t('gradient.angle') || 'Angle'}</label>
                <span className="text-sm text-brand-primary font-medium">{angle}°</span>
              </div>
              <input
                type="range"
                min={0}
                max={360}
                value={angle}
                onChange={e => setAngle(Number(e.target.value))}
                className="w-full"
              />
            </div>
          )}

          {/* Color Stops */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-foreground">{t('gradient.stops') || 'Color Stops'}</label>
              <button
                onClick={addStop}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-brand-primary text-white rounded-lg hover:opacity-90 transition-opacity"
              >
                <Plus className="w-3.5 h-3.5" />
                {t('gradient.addStop') || 'Add'}
              </button>
            </div>

            <div className="space-y-2">
              {stops.map(stop => (
                <div
                  key={stop.id}
                  className={`flex items-center gap-3 p-2 rounded-lg border ${
                    activeStopId === stop.id ? 'border-brand-primary bg-brand-primary/5' : 'border-border'
                  }`}
                  onClick={() => setActiveStopId(stop.id)}
                >
                  <input
                    type="color"
                    value={stop.color}
                    onChange={e => updateStop(stop.id, { color: e.target.value })}
                    className="w-10 h-10 rounded-lg border border-border cursor-pointer shrink-0"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono text-foreground">{stop.color}</span>
                      <span className="text-xs text-muted-foreground">{stop.position}%</span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={stop.position}
                      onChange={e => updateStop(stop.id, { position: Number(e.target.value) })}
                      className="w-full mt-1"
                    />
                  </div>
                  {stops.length > 2 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); removeStop(stop.id); }}
                      className="p-1.5 text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-md transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* CSS Output */}
          <div className="bg-black/5 dark:bg-white/5 rounded-lg p-3">
            <code className="text-sm font-mono text-foreground break-all">{cssDeclaration}</code>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={randomize}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-primary text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium"
            >
              <RefreshCw className="w-4 h-4" />
              {t('gradient.random') || 'Random'}
            </button>
            <button
              onClick={copyCSS}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-background border border-border text-foreground rounded-lg hover:bg-muted transition-colors text-sm font-medium"
            >
              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              {copied ? (t('actions.copied') || 'Copied!') : (t('actions.copy') || 'Copy CSS')}
            </button>
          </div>
        </div>

        {/* Presets */}
        <div className="bg-surface rounded-xl shadow-sm border border-border p-4 md:p-6">
          <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Palette className="w-4 h-4 text-brand-primary" />
            {t('gradient.presets') || 'Presets'}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {PRESETS.map(preset => (
              <button
                key={preset.name}
                onClick={() => loadPreset(preset)}
                className="group text-left"
              >
                <div
                  className="w-full aspect-[2/1] rounded-lg shadow-sm mb-2 transition-transform hover:scale-105"
                  style={{ background: generateGradientCSS(preset.type, preset.angle, preset.stops.map((s, i) => ({ ...s, id: String(i) }))) }}
                />
                <p className="text-xs text-muted-foreground">{preset.name}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Privacy Note */}
        <div className="text-center">
          <p className="text-xs text-muted-foreground">{t('security.note')}</p>
        </div>
      </main>

      <ContentSection />


      <Footer />
    </div>
  )
}

export default App
